import { GetOrderByEmailAndIdQuery } from 'gql'
import { useLazyQuery } from '@apollo/client'
import { NextPage } from 'next'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import { object, string } from 'yup'
import { useQueryParams } from '~hooks'
import { EMAIL } from 'constants/schema'
import { useEffect, useMemo } from 'react'
import { convertToClientTime } from '~utils/time'
import { textToTitleCase, formatCurrency, generateTicketTitle, getImageUrl } from '~utils'
import { Layout, Grid, Typography, Input, Button, Widget, Spinner, Link, Heading } from '~ui'

import MetaTags from 'components/shared/MetaTags'
import Body from 'components/account/tabs/orders/order-accordion/body'
import Header from 'components/account/tabs/orders/order-accordion/header'

import styles from 'styles/pages/Order-status.module.scss'
import { GET_ORDER_BY_EMAIL_AND_ID } from 'gql/order/queries'

const { Paragraph } = Typography

const formatOrderRejection = (reason = '', notes = '') => {
  if (reason && notes) return `Rejection reason: ${reason}. ${notes}`
  if (reason) return `Rejection reason: ${reason}`
  if (notes) return `Rejection reason: ${notes}`
  return ''
}

const OrderStatusPage: NextPage = () => {
  const [getOrder, { data, loading, error }] = useLazyQuery<GetOrderByEmailAndIdQuery>(
    GET_ORDER_BY_EMAIL_AND_ID,
    { notifyOnNetworkStatusChange: true }
  )

  const { query, replace } = useQueryParams()

  const order = data?.getOrderByEmailAndId?.order

  const { errors, handleSubmit, values, handleBlur, touched, handleChange } = useFormik({
    initialValues: {
      orderEmail: query.email || '',
      orderId: query.orderId || '',
    },
    onSubmit: async ({ orderEmail, orderId }) => {
      replace({ query: { email: orderEmail, orderId } })

      getOrder({
        variables: { email: orderEmail, id: Number(orderId) },
      })
    },
    validationSchema: object({
      orderEmail: EMAIL(),
      orderId: string().required('Please enter order id.'),
    }).required(),
  })

  useEffect(() => {
    if (query.email && query.orderId) {
      getOrder({ variables: { email: query.email, id: Number(query.orderId) } })
    }
  }, [])

  const orderNotes = useMemo(() => {
    if (order?.tevoOrder) {
      return formatOrderRejection(
        order?.tevoOrder.sellerRejectionReason,
        order?.tevoOrder.sellerRejectionNotes
      )
    }
    if (order?.status === 'PAYMENT_FAILED') {
      return 'Unable to process payment.'
    }

    return '' // add throw Error ?
  }, [order?.tevoOrder, order?.status])

  const orderTickets = useMemo(() => {
    if (order?.orderType === 'BUY_NOW') {
      return order?.orderitemSet.edges.map(
        ({
          node: {
            package: { name, shortDescription, image, deliveryNotes },
            price,
            quantity,
          },
        }) => ({
          title: name,

          price: formatCurrency(price),
          quantity,
          totalPrice: formatCurrency(price * quantity),

          image: getImageUrl(image),
          shortDescription,
          deliveryNotes,
        })
      )
    }

    if (order?.orderType === 'TEVO' && order?.tevoOrder) {
      const orderItem = order?.orderitemSet.edges[0].node

      return order?.tevoOrder.items.map(({ ticketGroup: { row, section } }) => ({
        title: generateTicketTitle(section, row),

        price: formatCurrency(orderItem.price),
        quantity: orderItem.quantity,
        totalPrice: formatCurrency(orderItem.price * orderItem.quantity),
      }))
    }

    return []
  }, [order?.orderType, order?.orderitemSet.edges, order?.tevoOrder])

  const tevoOrderItem = order?.tevoOrder?.items[0]

  return (
    <Layout>
      <MetaTags title="Order Status" />
      <Layout.Header grantedSecureBanner />
      <Layout.Main withPadding withSidebar={false}>
        <Layout.Main.Content>
          <Grid className={styles['main-layout']}>
            <Heading Tag="h3">Order info</Heading>

            <form onSubmit={handleSubmit} className={styles['form-layout']}>
              <Input
                type="email"
                id="orderEmail"
                name="orderEmail"
                onBlur={handleBlur}
                placeholder="Enter your email"
                error={touched.orderEmail && errors.orderEmail}
                onChange={handleChange}
                value={values.orderEmail}
              />

              <Input
                type="text"
                id="orderId"
                name="orderId"
                placeholder="Enter your order id"
                onBlur={handleBlur}
                error={touched.orderId && errors.orderId}
                onChange={handleChange}
                value={values.orderId}
              />

              <Button disabled={loading} type="submit" variant="primary">
                Get status
              </Button>
            </form>

            {error && <Paragraph>{error.message}</Paragraph>}

            {!data?.getOrderByEmailAndId && query.orderId && (
              <Paragraph>{`Order #${query.orderId} not found.`}</Paragraph>
            )}

            {order && !loading && (
              <Widget borderColor="dividers">
                <div className={styles.widget}>
                  <Header
                    pk={Number(order.pk)}
                    status={textToTitleCase(order.status)}
                    totalPrice={formatCurrency(order.totalPrice)}
                    refunded={order.totalRefunded}
                    createdAt={format(convertToClientTime(order.createdAt), 'dd MMM yyyy h:mm a')}
                  />
                  <hr className={styles.divider} />
                  <Body
                    orderNotes={orderNotes}
                    orderType={order?.orderType}
                    orderTickets={orderTickets}
                    shipping={order?.shippingPrice}
                    serviceFee={order?.serviceFee}
                    orderTax={order?.tax}
                    ticketType={tevoOrderItem?.ticketGroup.format}
                    deliveryStatus={order?.tevoOrder?.shipments[0]?.state}
                    ticketAvailableDate={tevoOrderItem?.ticketGroup.inHandOn}
                    orderItems={order?.orderitemSet.edges.map(({ node }) => node) || []}
                    ticketAvailable={!tevoOrderItem ? true : tevoOrderItem?.ticketGroup.inHand}
                    paymentMethod={
                      order?.pay?.paymentIntent?.paymentMethodName ||
                      order?.pay?.charge?.paymentMethodName
                    }
                  />
                </div>
              </Widget>
            )}

            {loading && (
              <Grid minHeight={400}>
                <Spinner center />
              </Grid>
            )}

            <Paragraph className={styles.footer}>
              Have questions about your order? Contact support at{' '}
              <Link href="mailto:support@granted.co" color="green">
                support@granted.co
              </Link>
            </Paragraph>
          </Grid>
        </Layout.Main.Content>
      </Layout.Main>
    </Layout>
  )
}

export default OrderStatusPage
